import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, FilePlus2, FileText, Menu, Zap, LogOut, User, UserCircle2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth, isAdmin } from '@/contexts/AuthContext';
import NotificationsBell from '@/components/NotificationsBell';
import { buildItems } from '@/components/layout/Sidebar';
import { useTicketsUnread } from '@/hooks/useTicketsUnread';

const crumbs = (pathname) => {
  const parts = pathname.split('/').filter(Boolean);
  const map = {
    partners: 'Aziende Partner', products: 'Catalogo Prodotti', contracts: 'Contratti',
    new: 'Nuovo', users: 'Utenti', listino: 'Listino & Preventivi',
    profile: 'Il mio profilo', tickets: 'Ticket & Assistenza',
  };
  const items = [{ to: '/', label: 'Dashboard' }];
  let acc = '';
  for (const p of parts) {
    acc += '/' + p;
    items.push({ to: acc, label: map[p] || p });
  }
  return items;
};

export const Topbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const items = crumbs(location.pathname);
  const currentLabel = items[items.length - 1]?.label || 'Dashboard';

  const isAdminUser = isAdmin(user);
  const logoBg = isAdminUser ? '#0A0E0B' : (user?.company?.primary_color || '#0A0E0B');
  const logoFg = isAdminUser ? '#9FFF5C' : (user?.company?.accent_color || '#9FFF5C');

  const navItems = buildItems(user);
  const ticketsUnread = useTicketsUnread(!!user, location.pathname);

  const roleLabel = { admin: 'Super Admin', partner: 'B.O.', area_manager: 'Area Manager', agent: 'Agente' };

  return (
    <header className="sticky top-0 z-30 h-14 border-b bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <div className="flex items-center justify-between gap-2 h-full px-3 sm:px-6">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden relative shrink-0" data-testid="mobile-nav-trigger">
                <Menu className="size-5" />
                {ticketsUnread > 0 && (
                  <span className="absolute top-1 right-1 size-2 rounded-full bg-primary" data-testid="mobile-nav-unread-dot" />
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[290px] p-0 flex flex-col">
              <div className="flex items-center gap-2 px-5 h-14 border-b shrink-0">
                <div className="size-9 rounded-xl grid place-items-center overflow-hidden" style={{ background: logoBg }}>
                  {!isAdminUser && user?.company?.logo_data_url ? <img src={user.company.logo_data_url} alt="" className="max-w-full max-h-full object-contain" /> : <Zap className="size-4" style={{ color: logoFg }} />}
                </div>
                <div><div className="font-heading text-base font-semibold">CRM Utility Boost</div></div>
              </div>
              <nav className="p-3 space-y-1 overflow-y-auto flex-1" data-testid="mobile-nav-menu">
                {navItems.map(item => {
                  const Icon = item.icon;
                  const active = item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to) && item.to !== '/';
                  return (
                    <SheetClose asChild key={item.to}>
                      <NavLink
                        to={item.to}
                        data-testid={`mobile-${item.testid}`}
                        className={cn(
                          'flex items-center gap-3 px-3 h-11 rounded-lg text-sm',
                          item.highlight && 'text-primary font-medium',
                          active ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60',
                        )}
                      >
                        <Icon className="size-4" />
                        <span className="truncate flex-1">{item.label}</span>
                        {item.testid === 'nav-tickets' && ticketsUnread > 0 && (
                          <span className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold grid place-items-center tabular-nums">
                            {ticketsUnread > 99 ? '99+' : ticketsUnread}
                          </span>
                        )}
                      </NavLink>
                    </SheetClose>
                  );
                })}
              </nav>
              <div className="border-t p-4 text-[11px] text-muted-foreground shrink-0">
                <div className="font-medium text-foreground truncate">{user?.full_name || user?.email}</div>
                <div>{roleLabel[user?.role]}</div>
              </div>
            </SheetContent>
          </Sheet>

          {/* Breadcrumb completo su >= sm; solo pagina corrente su mobile */}
          <nav className="hidden sm:flex items-center gap-1.5 text-sm text-muted-foreground min-w-0">
            {items.map((c, i) => (
              <React.Fragment key={c.to}>
                {i > 0 && <ChevronRight className="size-3.5 shrink-0" />}
                <Link to={c.to} className={cn('truncate hover:text-foreground', i === items.length - 1 && 'text-foreground font-medium')}>{c.label}</Link>
              </React.Fragment>
            ))}
          </nav>
          <span className="sm:hidden text-sm font-medium text-foreground truncate" data-testid="mobile-current-page">{currentLabel}</span>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <NotificationsBell />
          {/* Desktop: due pulsanti separati Preventivo / Contratto; Mobile: solo icone */}
          <Button asChild size="sm" variant="outline" className="hidden sm:inline-flex" data-testid="topbar-new-quote">
            <Link to="/contracts/new?mode=quote"><Sparkles className="size-4 mr-2" />Nuovo preventivo</Link>
          </Button>
          <Button asChild size="sm" className="hidden sm:inline-flex" data-testid="topbar-new-contract">
            <Link to="/contracts/new"><FilePlus2 className="size-4 mr-2" />Nuovo contratto</Link>
          </Button>
          <Button asChild size="icon" variant="outline" className="sm:hidden size-9" data-testid="topbar-new-quote-mobile" aria-label="Nuovo preventivo">
            <Link to="/contracts/new?mode=quote"><Sparkles className="size-4" /></Link>
          </Button>
          <Button asChild size="icon" className="sm:hidden size-9" data-testid="topbar-new-contract-mobile" aria-label="Nuovo contratto">
            <Link to="/contracts/new"><FilePlus2 className="size-4" /></Link>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="px-2.5 sm:px-3" data-testid="user-menu-trigger">
                <User className="size-4 sm:mr-2" />
                <span className="hidden sm:inline max-w-[140px] truncate">{user?.full_name || user?.email}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="text-sm font-medium">{user?.full_name}</div>
                <div className="text-xs text-muted-foreground">{user?.email}</div>
                <div className="text-xs text-muted-foreground mt-1">{roleLabel[user?.role]}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="goto-profile-button"><UserCircle2 className="size-4 mr-2" />Il mio profilo</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { logout(); navigate('/login'); }} data-testid="logout-button"><LogOut className="size-4 mr-2" />Esci</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
